export const GERMAN_TIMEZONE = "Europe/Berlin";

/**
 * Get the date string of today in German timezone
 * Used for business logic judgment, comparison, and interface parameter passing
 *
 * @returns A date string in `YYYY-MM-DD` format.
 */
export const getGermanTodayIso = (): string => {
  return new Intl.DateTimeFormat("en-CA", {
    timeZone: GERMAN_TIMEZONE,
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
  }).format(new Date());
};

/**
 * Get the date string of today in German timezone
 * Used for display in UI
 *
 * @returns A date string in `DD.MM.YYYY` format.
 */
export const getGermanTodayDisplay = (): string => {
  return new Intl.DateTimeFormat("de-DE", {
    timeZone: GERMAN_TIMEZONE,
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
  }).format(new Date());
};

/**
 * Get the date object of today in German timezone
 * Used for initialize a new patient
 *
 * @returns a german Date instance with hours, minutes, and seconds all set to 00:00:00
 */
export const getGermanTodayDateObject = (): Date => {
  const germanIso = getGermanTodayIso();
  const [year, month, day] = germanIso.split("-").map(Number);
  return new Date(year, month - 1, day);
};

/**
 * Formats a raw date input value by replacing `/` with /.`
 *
 * checks the existence of event target's value and mutates the DOM input directly
 * @param event - Optional React mouse or keyboard event
 */
export function handleRawDateInput(
  event?: React.MouseEvent<HTMLElement> | React.KeyboardEvent<HTMLElement>,
) {
  const input = event?.currentTarget as HTMLInputElement | undefined;
  const value = input?.value;
  if (!value) return;
  const dotFormat = value.replace(/\//g, ".");
  if (input) input.value = dotFormat;
}

/**
 * formats a date-only string from "YYYY-MM-DD" to German "DD.MM.YYYY" format without Date Object
 *
 * This avoids default Date constructore behavior, which interprets the date string
 * as UTC and could cause timezone offset
 *
 * @param dateOnlyString - A string representing the date in "YYYY-MM-DD" format
 */
export function formatCalendarDate(dateOnlyString: string | undefined): string {
  if (!dateOnlyString) return "";

  const match = dateOnlyString.match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (!match) {
    console.warn(
      `[formatCalendarDate] Malformed date string: "${dateOnlyString}"`,
    );
    return "";
  }

  const [, year, month, day] = match;
  return `${day}.${month}.${year}`;
}

/**
 * Formats an ISO date-time string into German calendar date and time representation (`DD.MM.YYYY, HH:mm`).
 * @param isoString an ISO formatted date-time string (e.g., `"2026-08-13T22:20:35"`).
 * @returns German formatted date and time "e.g. 13.08.2026, 22:20"
 */
export function formatDateTime(isoString: string | undefined): string {
  if (!isoString) return "";

  const match = isoString.match(/^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2})$/);
  if (!match) {
    console.warn(
      `[formatDateTime] Incomplete date/time components in "${isoString}"`,
    );
    return "";
  }
  const [, year, month, day, hour, minute] = match;

  //derive the abbreviation of German timezone
  // Note: might have 1h boundary offest during DST transition nights, but the time display is always right
  let tzSuffix = "";
  try {
    const date = new Date(`${year}-${month}-${day}T${hour}:${minute}:00Z`);
    const parts = new Intl.DateTimeFormat("de-DE", {
      timeZone: GERMAN_TIMEZONE,
      timeZoneName: "short",
    }).formatToParts(date);

    const tzPart = parts.find((p) => p.type === "timeZoneName");
    if (tzPart) {
      tzSuffix = ` (${tzPart.value})`;
    }
  } catch (error) {
    console.warn(`[formatDateTime] Failed to resolve timezone name:`, error);
  }

  return `${day}.${month}.${year}, ${hour}:${minute}${tzSuffix}`;
}

/**
 * Formats a `Date` object to a calendar date string "YYYY-MM-DD"
 * @param date the date object to format
 * @returns A date string in `YYYY-MM-DD` format.
 */
export const formatLocalDate = (date: Date | null | undefined): string => {
  if (!date || isNaN(date.getTime())) return "";

  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
};

/**
 * parses a date-only string from "YYYY-MM-DD" format into js Date Object
 *
 * This avoids default Date constructore behavior, which interprets the date string
 * as UTC and could cause timezone offset
 *
 * @param dateOnlyString - A string representing the date in "YYYY-MM-DD" format
 */
export function parseLocalDate(dateOnlyString: string): Date {
  // regex check for YYYY-MM-DD format
  if (!/^\d{4}-\d{2}-\d{2}$/.test(dateOnlyString)) {
    throw new Error(
      `[parseLocalDate] Received malformed date string: "${dateOnlyString}"`,
    );
  }

  const [year, month, day] = dateOnlyString.split("-").map(Number);
  return new Date(year, month - 1, day);
}
